import React, { createContext, useContext, useState } from 'react';
import { Lang, DEFAULT_LANG, STORAGE_KEY } from './types';

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LanguageContext = createContext<LangContextValue>({
  lang: DEFAULT_LANG,
  setLang: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as Lang;
    return stored && (['cs', 'en', 'vi', 'uk'] as Lang[]).includes(stored) ? stored : DEFAULT_LANG;
  });

  const setLang = (l: Lang) => {
    localStorage.setItem(STORAGE_KEY, l);
    setLangState(l);
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}

/**
 * A part of a page that speaks its own language rather than the app's: the guest Guide has its own
 * cs/en switch, and the sections it embeds (deposit, extra hours) read `useLang()`. Without this
 * they followed the app language stored in the browser — so a guest who switched the Guide to
 * English still got those sections in Czech, the ones that ask them to pay.
 */
export function LanguageOverride({ lang, children }: { lang: Lang; children: React.ReactNode }) {
  const { setLang } = useLang();
  return (
    <LanguageContext.Provider value={{ lang, setLang }}>
      {children}
    </LanguageContext.Provider>
  );
}
